import { FC, useEffect, useMemo, useRef } from 'react';

export type BackgroundVideoProps = {
  /**
   * Label of the button
   */
  name: string;
  /**
   * `true` if the video should span the whole screen
   *
   * @default false
   */
  fullscreen?: boolean;
  /**
   * Array of available resolutions in Firebase Storage
   *
   * Please upload the videos upfront to Firebase Storage in all environments
   *
   * @default [480, 720, 1080]
   */
  resolutions?: number[];
  /**
   * Array of available formats in Firebase Storage
   *
   * Please upload the videos upfront to Firebase Storage in all environments
   *
   * @default ['webm', 'mp4']
   */
  formats?: string[];
  /**
   * additional classes for the video
   */
  className?: string;
  /**
   * video speed
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/playbackRate
   *
   * @default 1
   */
  playbackRate?: number;
};

const getUrlForVideo = (name: string, resolution: number, format: string) => {
  const assetUrlPart = encodeURIComponent(
    `assets/videos/${name}-${resolution}p.${format}`,
  );
  return `https://firebasestorage.googleapis.com/v0/b/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/o/${assetUrlPart}?alt=media`;
};

export const BackgroundVideo: FC<BackgroundVideoProps> = ({
  name,
  fullscreen = false,
  resolutions = [480, 720, 1080],
  formats = ['webm', 'mp4'],
  className = '',
  playbackRate = 1,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!videoRef.current) return;

    videoRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  const sources = useMemo(
    () =>
      [...resolutions]
        .sort((a, b) => a - b)
        .reverse()
        .map((resolution, index) =>
          formats.map((format) => (
            <source
              key={resolution + format}
              src={getUrlForVideo(name, resolution, format)}
              type={`video/${format}`}
              media={index ? `all and (max-width: ${resolution}px)` : undefined}
            />
          )),
        )
        .flat()
        .reverse(),
    [formats, name, resolutions],
  );

  className += useMemo(
    () =>
      fullscreen ? 'h-screen fixed object-cover -z-1 top-0 left-0 w-full' : '',
    [fullscreen],
  );

  return (
    <video ref={videoRef} playsInline autoPlay muted loop className={className}>
      {sources}
    </video>
  );
};
